import { useState, useEffect, useCallback } from 'react'
import { Joyride, ACTIONS, EVENTS, STATUS } from 'react-joyride'
import type { EventData } from 'react-joyride'
import { useOnboarding } from '../hooks/useOnboarding'
import { OnboardingTooltip } from './OnboardingTooltip'

const MOBILE_BREAKPOINT = '(max-width: 767px)'

interface OnboardingTourProps {
  run: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

export function OnboardingTour({ run: externalRun, onComplete, onSkip }: OnboardingTourProps) {
  const { steps, handleMarkCompleted, handleOptOut: handleOptedOut } = useOnboarding()
  const [stepIndex, setStepIndex] = useState(0)
  const [internalRun, setInternalRun] = useState(externalRun)
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_BREAKPOINT).matches)

  useEffect(() => {
    setInternalRun(externalRun)
  }, [externalRun])

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_BREAKPOINT)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  if (!steps.length || isMobile) return null

  const handleEvent = useCallback((data: EventData) => {
    const { status, action, index, type } = data

    if (status === STATUS.FINISHED) {
      handleMarkCompleted()
      onComplete()
      setStepIndex(0)
      return
    }

    if (status === STATUS.SKIPPED) {
      handleOptedOut()
      onSkip()
      setStepIndex(0)
      return
    }

    if (type === EVENTS.STEP_AFTER) {
      if (action === ACTIONS.NEXT) {
        setStepIndex(index + 1)
      } else if (action === ACTIONS.PREV) {
        setStepIndex(index - 1)
      }
    }

    if (action === ACTIONS.CLOSE) {
      setInternalRun(false)
      handleOptedOut()
      onSkip()
    }
  }, [handleMarkCompleted, handleOptedOut, onComplete, onSkip])

  return (
    <Joyride
      steps={steps}
      run={internalRun}
      stepIndex={stepIndex}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      tooltipComponent={OnboardingTooltip}
      options={{
        overlayColor: 'rgba(15, 23, 42, 0.55)',
        zIndex: 10000,
        spotlightRadius: 12,
        overlayClickAction: false,
        arrowColor: '#ffffff',
        scrollOffset: 120,
      }}
      styles={{
        overlay: {
          transition: 'opacity 0.4s ease',
        },
        floater: {
          transition: 'opacity 0.3s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.15))',
        },
      }}
    />
  )
}
